/**
 * Icon set. Two families:
 *  - line icons (Icon*)   : stroke = currentColor, for nav / actions / buttons. Size them with CSS (width/height) or the `size` prop.
 *  - colour icons (Res*)  : full-colour "sticker" icons with a dark navy outline, for resources. Not themed by currentColor.
 */
import type { Resources } from '@pk/shared';
import type { ReactNode } from 'react';

export type IconProps = { size?: number; className?: string };

const line = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;
function L({ size, className, children }: IconProps & { children: ReactNode }) {
  return <svg viewBox="0 0 24 24" width={size} height={size} className={className} aria-hidden="true" focusable="false" {...line}>{children}</svg>;
}

export const IconHouse = (p: IconProps) => (<L {...p}><path d="M3 11l9-7 9 7M5 10v10h14V10M10 20v-6h4v6" /></L>);
export const IconRealm = (p: IconProps) => (<L {...p}><path d="M4 18l2-9 4 4 2-8 2 8 4-4 2 9zM4 21h16" /></L>);
export const IconMap = (p: IconProps) => (<L {...p}><path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2zM9 4v14M15 6v14" /></L>);
export const IconWar = (p: IconProps) => (<L {...p}><path d="M5 19L18 6M14 4h6v6M19 19L6 6M4 4h6M4 4v6M8 16l-3 3M16 16l3 3" /></L>);
export const IconLetter = (p: IconProps) => (<L {...p}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 7 9-7" /></L>);
export const IconBook = (p: IconProps) => (<L {...p}><path d="M5 4h11a3 3 0 013 3v13H8a3 3 0 01-3-3zM5 17a3 3 0 013-3h11" /></L>);
export const IconGear = (p: IconProps) => (<L {...p}><circle cx="12" cy="12" r="3" /><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" /></L>);
export const IconSearch = (p: IconProps) => (<L {...p}><circle cx="10.5" cy="10.5" r="6" /><path d="M15 15l5.5 5.5" /></L>);
export const IconTree = (p: IconProps) => (<L {...p}><rect x="9" y="3" width="6" height="5" rx="1" /><rect x="3" y="16" width="6" height="5" rx="1" /><rect x="15" y="16" width="6" height="5" rx="1" /><path d="M12 8v4M6 16v-4h12v4" /></L>);
export const IconQuill = (p: IconProps) => (<L {...p}><path d="M20 4c-8 0-13 5-13 12v2l3-3c6 0 10-5 10-11zM4 21l5-5M10 15h5" /></L>);
export const IconAlert = (p: IconProps) => (<L {...p}><path d="M12 3l10 18H2zM12 10v5M12 18v.5" /></L>);
export const IconCheck = (p: IconProps) => (<L {...p}><path d="M4 12.5l5 5L20 6.5" /></L>);
export const IconDagger = (p: IconProps) => (<L {...p}><path d="M14 3l7 7-9 9-3-3 5-5-5 5-2 2-3-3 2-2 5-5M6 18l-3 3" /></L>);
export const IconUsers = (p: IconProps) => (<L {...p}><circle cx="9" cy="8" r="3.2" /><path d="M3 20c0-3.5 2.7-6 6-6s6 2.5 6 6M16 5.2a3 3 0 010 5.6M18 14.3c1.7.8 3 2.6 3 5.7" /></L>);
export const IconHourglass = (p: IconProps) => (<L {...p}><path d="M6 3h12M6 21h12M7 3c0 5 5 6 5 9s-5 4-5 9M17 3c0 5-5 6-5 9s5 4 5 9" /></L>);
export const IconChevron = (p: IconProps) => (<L {...p}><path d="M9 5l7 7-7 7" /></L>);
export const IconShield = (p: IconProps) => (<L {...p}><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" /></L>);
export const IconScroll = (p: IconProps) => (<L {...p}><path d="M7 4h11a2 2 0 012 2v1H9M7 4a2 2 0 00-2 2v12a2 2 0 002 2h11a2 2 0 002-2V7M9 11h7M9 15h5" /></L>);
export const IconChat = (p: IconProps) => (<L {...p}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></L>);
export const IconCrown = (p: IconProps) => (<L {...p}><path d="M3 8l4.5 4L12 5l4.5 7L21 8l-2 11H5zM5.5 21h13" /></L>);
export const IconPlus = (p: IconProps) => (<L {...p}><path d="M12 5v14M5 12h14" /></L>);
export const IconX = (p: IconProps) => (<L {...p}><path d="M5 5l14 14M19 5L5 19" /></L>);
export const IconBanner = (p: IconProps) => (<L {...p}><path d="M6 21V3M6 4h12l-3 4 3 4H6" /></L>);
export const IconHammer = (p: IconProps) => (<L {...p}><path d="M14 4l6 6-3 3-6-6zM12 8L4 16l4 4 8-8M3 21h8" /></L>);
export const IconCart = (p: IconProps) => (<L {...p}><path d="M3 5h3l2 11h10l2-8H7" /><circle cx="9" cy="20" r="1.4" /><circle cx="17" cy="20" r="1.4" /></L>);
export const IconBank = (p: IconProps) => (<L {...p}><path d="M3 9l9-5 9 5zM5 10v8M9.500 10v8M14.500 10v8M19 10v8M3 21h18" /></L>);
export const IconPact = (p: IconProps) => (<L {...p}><path d="M3 12l4-4 4 2 3-2 3 2 4-2M3 12l5 6 2-1 2 2 2-1 2 1 3-3M12 10l-2 3" /></L>);
export const IconStall = (p: IconProps) => (<L {...p}><path d="M3 9l2-5h14l2 5M3 9h18v2a3 3 0 01-6 0 3 3 0 01-6 0 3 3 0 01-6 0zM5 14v6h14v-6" /></L>);
export const IconMask = (p: IconProps) => (<L {...p}><path d="M4 5h16v6c0 5-3.500 8-8 9-4.500-1-8-4-8-9zM8 10h2M14 10h2M9 15c1 1 5 1 6 0" /></L>);
export const IconSword = (p: IconProps) => (<L {...p}><path d="M5 19L19 5M15 5h4v4M7 14l3 3M4 20l2-2" /></L>);

/* ------------------------------------------------------------ colour icons */
const O = '#0b1b33';
function C({ size, className, children }: IconProps & { children: ReactNode }) {
  return <svg viewBox="0 0 24 24" width={size} height={size} className={className} aria-hidden="true" focusable="false" strokeLinejoin="round" strokeLinecap="round">{children}</svg>;
}

export const ResGold = (p: IconProps) => (
  <C {...p}>
    <circle cx="12" cy="12" r="9.5" fill="#f2b631" stroke={O} strokeWidth="1.8" />
    <circle cx="12" cy="12" r="6.2" fill="#ffd46b" stroke="#c27f1e" strokeWidth="1.5" />
    <path d="M12 7.800v8.400M13.800 9.600c-.6-.9-3.700-1-3.700.9 0 1.900 3.800 1 3.800 3 0 1.900-3.200 1.800-3.800.8" fill="none" stroke="#c27f1e" strokeWidth="1.3" />
    <path d="M6.200 8.200a7 7 0 013-2.600" fill="none" stroke="#fff" strokeOpacity=".7" strokeWidth="1.400" />
  </C>
);
export const ResFood = (p: IconProps) => (
  <C {...p}>
    <path d="M12 8v14" stroke="#8a5a1a" strokeWidth="2.2" fill="none" />
    <g fill="#ffd46b" stroke={O} strokeWidth="1.5">
      <path d="M12 1.800c2 1.900 2 4.400 0 6.200c-2-1.800-2-4.300 0-6.200z" />
      <path d="M11 11c-3 .3-4.800-1.500-5-4.500c3-.3 4.800 1.500 5 4.500z" />
      <path d="M13 11c3 .3 4.800-1.500 5-4.500c-3-.3-4.800 1.500-5 4.500z" />
      <g transform="translate(0 5.500)">
        <path d="M11 11c-3 .3-4.800-1.500-5-4.500c3-.3 4.800 1.500 5 4.500z" />
        <path d="M13 11c3 .3 4.800-1.500 5-4.500c-3-.3-4.800 1.500-5 4.500z" />
      </g>
    </g>
  </C>
);
export const ResTimber = (p: IconProps) => (
  <C {...p}>
    <g fill="#b9722f" stroke={O} strokeWidth="1.700">
      <circle cx="7" cy="16" r="4.600" /><circle cx="17" cy="16" r="4.600" /><circle cx="12" cy="8.500" r="4.600" />
    </g>
    <g fill="#e8b073" stroke="#8a4f1c" strokeWidth="1.200">
      <circle cx="7" cy="16" r="2.200" /><circle cx="17" cy="16" r="2.200" /><circle cx="12" cy="8.500" r="2.200" />
    </g>
  </C>
);
export const ResStone = (p: IconProps) => (
  <C {...p}>
    <path d="M2.500 19L5.500 9.500L12 4.500L19 8L21.500 19Z" fill="#9aa5b5" stroke={O} strokeWidth="1.800" />
    <path d="M5.500 9.500L12 12L19 8M12 12L10 19M12 12L16 19" fill="none" stroke="#dbe3ee" strokeWidth="1.300" />
    <path d="M5.500 9.500L12 4.500L19 8L12 12Z" fill="#c9d2de" />
  </C>
);
export const ResIron = (p: IconProps) => (
  <C {...p}>
    <path d="M3.500 18L6.800 10H17.200L20.500 18Z" fill="#6f86a8" stroke={O} strokeWidth="1.800" />
    <path d="M6.800 10L8.800 6H15.200L17.200 10Z" fill="#b4c5dc" stroke={O} strokeWidth="1.800" />
    <path d="M7 15.500h10" stroke="#9fb3d0" strokeWidth="1.200" />
  </C>
);
export const ResHorses = (p: IconProps) => (
  <C {...p}>
    <path d="M6 21V12a6 6 0 0112 0v9" fill="none" stroke={O} strokeWidth="6.200" />
    <path d="M6 21V12a6 6 0 0112 0v9" fill="none" stroke="#c9d2de" strokeWidth="3" />
    <g fill={O}><circle cx="6" cy="15" r=".9" /><circle cx="6.800" cy="9.500" r=".9" /><circle cx="12" cy="6" r=".9" /><circle cx="17.200" cy="9.500" r=".9" /><circle cx="18" cy="15" r=".9" /></g>
  </C>
);
export const ResWool = (p: IconProps) => (
  <C {...p}>
    <g fill={O} stroke={O} strokeWidth="3.200">
      <circle cx="7.500" cy="13" r="4" /><circle cx="12.500" cy="9.500" r="4.600" /><circle cx="17" cy="14" r="3.800" /><circle cx="11.500" cy="16.500" r="3.800" />
    </g>
    <g fill="#f4f7fb">
      <circle cx="7.500" cy="13" r="4" /><circle cx="12.500" cy="9.500" r="4.600" /><circle cx="17" cy="14" r="3.800" /><circle cx="11.500" cy="16.500" r="3.800" />
    </g>
    <path d="M9 9.500c1-1.200 2.200-1.600 3.500-1.400" fill="none" stroke="#c9d2de" strokeWidth="1.200" />
  </C>
);
export const ResMen = (p: IconProps) => (
  <C {...p}>
    <path d="M4.500 15a7.500 7.500 0 0115 0v5h-15z" fill="#aebccf" stroke={O} strokeWidth="1.800" />
    <path d="M12 3.500v5" stroke="#d94a40" strokeWidth="2.600" />
    <rect x="7" y="12.500" width="10" height="3" rx="1" fill={O} />
    <path d="M12 15.500V20" stroke={O} strokeWidth="1.400" />
    <path d="M6.500 11.500a6 6 0 013-3.700" fill="none" stroke="#fff" strokeOpacity=".7" strokeWidth="1.300" />
  </C>
);
export const ResPrestige = (p: IconProps) => (
  <C {...p}>
    <path d="M3 8.500l4.500 4L12 5l4.500 7.500L21 8.500l-2 11H5z" fill="#f2b631" stroke={O} strokeWidth="1.800" />
    <path d="M5.500 17h13" stroke="#c27f1e" strokeWidth="1.600" />
    <circle cx="12" cy="4.500" r="1.400" fill="#ffd46b" stroke={O} strokeWidth="1.200" />
  </C>
);

const RES_ICONS: Record<keyof Resources, (p: IconProps) => ReactNode> = {
  gold: ResGold, food: ResFood, timber: ResTimber, stone: ResStone, iron: ResIron, horses: ResHorses, wool: ResWool, manpower: ResMen,
};
/** Colour icon for a resource key (gold, food, timber, stone, iron, horses, wool, manpower). */
export function ResourceIcon({ k, size, className }: { k: keyof Resources } & IconProps) {
  const I = RES_ICONS[k];
  return <>{I({ size, className })}</>;
}
